import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, Search, Download } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';
import { PeriodFilter } from '../components/dashboard/PeriodFilter';

const API = process.env.REACT_APP_BACKEND_URL || '';
const AT_BENCHMARK_TOLERANCE = 1;
const ROW_CAP = 500;
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const GUIDE = {
  sections: [
    { title: 'Entity toggle', desc: 'Switch between SIL, SKM, or Both to control whose margin vs the Aqua Brahma market rate drives the heatmap, deep-dive and overpay log below.' },
    { title: 'Margin heatmap', desc: 'Grade × time grid. Green = bought below market (savings), pink = bought above market (overpay). Toggle Day/Week/Month to change granularity.' },
    { title: 'Grade deep-dive', desc: 'Pick a grade to see its price history vs the Aqua Brahma benchmark, plus daily movement — how far above or below market you traded each day.' },
    { title: 'Overpay log', desc: 'Every lot where the selected entity paid above the market rate, worst first. Search by date/grade, sort any column, or export to CSV.' },
  ],
  lookFor: 'Clusters of pink cells in the heatmap or a long tail in the overpay log point to grades or periods worth reviewing with the procurement desk.',
};

/* ── Helpers (ported from the legacy grade-strategy.html) ── */
const countToNum = (c) => { const m = String(c).match(/\d+/); return m ? +m[0] : 999; };
const parseISODate = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d); };
const fmtDate = (d) => d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
const fmtDateLong = (d) => d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtMonth = (d) => d ? d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—';
const fmtAxisDate = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtDateInput = (d) => { const pad = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const fmtPrice = (n) => (n == null || isNaN(n)) ? '—' : '₹' + Math.round(n);
const fmtPricePrecise = (n) => (n == null || isNaN(n)) ? '—' : '₹' + (+n).toFixed(2);
const fmtDelta = (n) => (n == null || isNaN(n)) ? '—' : (n >= 0 ? '+' : '−') + '₹' + Math.abs(n).toFixed(2);
const fmtInt = (n) => (n == null || isNaN(n)) ? '—' : Math.round(n).toLocaleString();
const mean = (arr) => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
const groupBy = (arr, keyFn) => { const m = new Map(); arr.forEach(r => { const k = keyFn(r); if (!m.has(k)) m.set(k, []); m.get(k).push(r); }); return m; };
const weekKey = (d) => { const dt = new Date(d); const day = dt.getDay() || 7; dt.setDate(dt.getDate() - day + 1); dt.setHours(0, 0, 0, 0); return dt.getTime(); };
const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
const entityPrice = (r, ent) => ent === 'SIL' ? r.sil : ent === 'SKM' ? r.skm : (r.sil + r.skm) / 2;
const entityMargin = (r, ent) => entityPrice(r, ent) - r.aqua;
const silMargin = (r) => r.sil - r.aqua;
const skmMargin = (r) => r.skm - r.aqua;

const ENTITY_TABS = [
  { id: 'SIL', label: 'SIL', dot: 'var(--dash-blue)' },
  { id: 'SKM', label: 'SKM', dot: 'var(--dash-coral)' },
  { id: 'both', label: 'Both', dot: 'linear-gradient(90deg, var(--dash-blue), var(--dash-coral))' },
];

const OVERPAY_COLUMNS = [
  { key: 'overpay', label: 'Overpay ₹/kg' },
  { key: 'date', label: 'Date' },
  { key: 'count', label: 'Grade' },
  { key: 'paid', label: 'Paid' },
  { key: 'aqua', label: 'Market rate' },
  { key: 'pct', label: 'Overpay %' },
  { key: 'other', label: 'Other entity' },
];

/* ── Small disclosure/accordion panel, matching the legacy .drill component ── */
const Disclosure = ({ title, tag, hint, open, onToggle, children }) => (
  <div className="bg-dash-panel border border-dash-line rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-5 py-4 text-left cursor-pointer"
      data-testid={`disclosure-${tag}`}
    >
      <span className={`w-6 h-6 rounded-md bg-dash-panel2 border border-dash-line flex items-center justify-center shrink-0 transition-transform duration-200 ${open ? 'rotate-90 text-dash-ice border-dash-ice' : 'text-dash-mut'}`}>
        <ChevronRight className="w-3.5 h-3.5" />
      </span>
      <h3 className="font-grotesk text-base font-bold text-dash-ink">{title}</h3>
      <span className="font-mono text-[10px] px-2 py-0.5 rounded-full border border-dash-line text-dash-mut">{tag}</span>
      <span className="ml-auto text-xs text-dash-mut italic hidden sm:block">{hint}</span>
    </button>
    {open && <div className="px-5 pb-5 pt-1 border-t border-dash-line">{children}</div>}
  </div>
);

/* ── Skeleton (loading state) — mirrors this page's header/period-filter/disclosure
   shape, same shimmer-bone convention as DashboardSkeleton.jsx / InvoicePISkeleton ── */
const SB = ({ className = '', style, delay = 0 }) => (
  <div className={`skel-bone skel-pill ${className}`} style={{ ...style, animationDelay: `${delay}ms` }} />
);

const DisclosureSkeleton = ({ delay = 0 }) => (
  <div className="bg-dash-panel border border-dash-line rounded-xl overflow-hidden">
    <div className="w-full flex items-center gap-3 px-5 py-4">
      <div className="skel-bone" style={{ width: 24, height: 24, borderRadius: 6, animationDelay: `${delay}ms` }} />
      <SB className="h-3.5 w-36" delay={delay + 20} />
      <SB className="h-3 w-8 !rounded-full" delay={delay + 40} />
      <SB className="h-2.5 w-56 ml-auto hidden sm:block" delay={delay + 60} />
    </div>
  </div>
);

const GradeStrategySkeleton = () => (
  <div className="min-h-full bg-dash-bg text-dash-ink font-grotesk antialiased" data-testid="grade-strategy-skeleton">
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3 bg-dash-panel p-6 rounded-xl border border-dash-line shadow-sm">
        <div className="skel-bone" style={{ width: 36, height: 36, borderRadius: 8, animationDelay: '0ms' }} />
        <div className="flex-1">
          <SB className="h-4 w-32 mb-2" delay={20} />
          <SB className="h-2.5 w-72" delay={40} />
        </div>
        <div className="flex items-center gap-1 bg-dash-panel2 border border-dash-line rounded-lg p-1">
          {[0, 1, 2].map(i => <SB key={i} className="h-7 w-14 !rounded-md" delay={60 + i * 20} />)}
        </div>
      </div>

      {/* Period Filter */}
      <div className="bg-dash-panel p-4 rounded-xl border border-dash-line shadow-sm flex items-center gap-2.5">
        <SB className="h-2.5 w-12" delay={140} />
        <SB className="h-8 w-28 !rounded-md" delay={160} />
        <SB className="h-2.5 w-4" delay={170} />
        <SB className="h-8 w-28 !rounded-md" delay={180} />
        <SB className="h-8 w-16 !rounded-md" delay={200} />
        <SB className="h-2.5 w-48 ml-auto" delay={220} />
      </div>

      {/* Drill-down intro */}
      <div className="bg-dash-panel border border-dash-line border-dashed rounded-xl p-4">
        <SB className="h-2.5 w-full mb-1.5" delay={240} />
        <SB className="h-2.5 w-2/3" delay={260} />
      </div>

      {/* Three collapsed disclosure panels */}
      <DisclosureSkeleton delay={280} />
      <DisclosureSkeleton delay={300} />
      <DisclosureSkeleton delay={320} />
    </div>
  </div>
);

const PriceHistoryTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-dash-panel border border-dash-line rounded-lg p-2.5 text-[11px] shadow-xl min-w-[150px]">
      <div className="font-bold text-dash-ink mb-1">{d.dateLabel}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex justify-between gap-4">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-mono text-dash-ink">{fmtPricePrecise(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const DailyTooltip = ({ active, payload, ent }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-dash-panel border border-dash-line rounded-lg p-2.5 text-[11px] shadow-xl min-w-[170px]">
      <div className="font-bold text-dash-ink mb-1">{d.dateLabel}</div>
      <div className="flex justify-between gap-4">
        <span className="text-dash-mut">{ent}:</span>
        <span className="font-mono text-dash-ink">{fmtDelta(d.y)}/kg vs market</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-dash-mut">Lots:</span>
        <span className="font-mono text-dash-ink">{d.lots}</span>
      </div>
    </div>
  );
};

const GradeStrategyReact = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [entity, setEntity] = useState('SIL');
  const [heatmapAgg, setHeatmapAgg] = useState('month');
  const [gradeFocus, setGradeFocus] = useState('');

  // Period filter — months derived from the actual data present, same month-index
  // band pattern (fromIdx/toIdx + apply/reset) as the other dashboards' PeriodFilter.
  const [months, setMonths] = useState([]); // [{ label: 'May-2023', year, month }]
  const [fromIdx, setFromIdx] = useState(0);
  const [toIdx, setToIdx] = useState(0);
  const [appliedRange, setAppliedRange] = useState([0, 0]);
  const [isFiltered, setIsFiltered] = useState(false);

  const [overpaySearch, setOverpaySearch] = useState('');
  const [overpaySortKey, setOverpaySortKey] = useState('overpay');
  const [overpaySortAsc, setOverpaySortAsc] = useState(false);

  const [openHeatmap, setOpenHeatmap] = useState(false);
  const [openDeepDive, setOpenDeepDive] = useState(false);
  const [openOverpay, setOpenOverpay] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/api/grade-strategy/data`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = await res.json();
        const rows = (payload.rows || []).map((r) => {
          const d = parseISODate(r.date);
          return { date: d, t: d.getTime(), count: String(r.count), sil: +r.sil || 0, aqua: +r.aqua || 0, skm: +r.skm || 0, countSort: countToNum(r.count) };
        }).sort((a, b) => a.t - b.t || a.countSort - b.countSort);
        setRawData(rows);

        // Build the chronological list of distinct months present in the data
        const seen = new Map();
        rows.forEach((r) => {
          const key = `${r.date.getFullYear()}-${r.date.getMonth() + 1}`;
          if (!seen.has(key)) seen.set(key, { year: r.date.getFullYear(), month: r.date.getMonth() + 1 });
        });
        const mm = [...seen.values()]
          .sort((a, b) => (a.year - b.year) || (a.month - b.month))
          .map((m) => ({ ...m, label: `${MONTH_ABBR[m.month - 1]}-${m.year}` }));
        const last = Math.max(0, mm.length - 1);
        setMonths(mm);
        setToIdx(last);
        setAppliedRange([0, last]);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const applyPeriod = () => {
    if (fromIdx > toIdx) { alert('From must be before To'); return; }
    setAppliedRange([fromIdx, toIdx]);
    setIsFiltered(!(fromIdx === 0 && toIdx === months.length - 1));
  };

  const resetPeriod = () => {
    const last = Math.max(0, months.length - 1);
    setFromIdx(0); setToIdx(last);
    setAppliedRange([0, last]);
    setIsFiltered(false);
  };

  const periodLabel = months.length === 0 ? '' : isFiltered
    ? `${months[appliedRange[0]].label} to ${months[appliedRange[1]].label}`
    : `All ${months.length} month${months.length === 1 ? '' : 's'} · ${months[0].label} to ${months[months.length - 1].label}`;

  // Data restricted to the applied period band — everything below (grade options,
  // heatmap, deep-dive, overpay log) is derived from this instead of rawData directly.
  const filteredData = useMemo(() => {
    if (!isFiltered || months.length === 0) return rawData;
    const from = months[appliedRange[0]];
    const to = months[appliedRange[1]];
    if (!from || !to) return rawData;
    const start = new Date(from.year, from.month - 1, 1).getTime();
    const end = new Date(to.year, to.month, 0, 23, 59, 59).getTime();
    return rawData.filter(r => r.t >= start && r.t <= end);
  }, [rawData, isFiltered, months, appliedRange]);

  const gradeOptions = useMemo(
    () => [...new Set(filteredData.map(r => r.count))].sort((a, b) => countToNum(a) - countToNum(b)),
    [filteredData]
  );

  useEffect(() => {
    if (gradeOptions.length && !gradeOptions.includes(gradeFocus)) setGradeFocus(gradeOptions[0]);
  }, [gradeOptions, gradeFocus]);

  const entLabel = entity === 'both' ? 'SIL' : entity;

  /* ── Heatmap ── */
  const heatmap = useMemo(() => {
    if (!filteredData.length) return null;
    const ent = entLabel;
    const bucketFmt = heatmapAgg === 'day' ? fmtDate
      : heatmapAgg === 'week' ? (d => 'W' + getISOWeek(d) + "'" + String(d.getFullYear()).slice(-2))
      : fmtMonth;

    const buckets = new Map();
    const bucketDates = new Map();
    filteredData.forEach((r) => {
      let key, repDate;
      if (heatmapAgg === 'day') { key = r.t; repDate = r.date; }
      else if (heatmapAgg === 'week') { key = weekKey(r.date); repDate = new Date(key); }
      else { key = monthKey(r.date); const [y, m] = key.split('-').map(Number); repDate = new Date(y, m, 1); }
      if (!buckets.has(key)) { buckets.set(key, new Map()); bucketDates.set(key, repDate); }
      const b = buckets.get(key);
      if (!b.has(r.count)) b.set(r.count, []);
      b.get(r.count).push(entityMargin(r, ent));
    });

    const bucketKeys = [...buckets.keys()].sort((a, b) => bucketDates.get(a) - bucketDates.get(b));
    const cellMap = {};
    gradeOptions.forEach((g) => {
      cellMap[g] = {};
      bucketKeys.forEach((bk) => {
        const margins = buckets.get(bk).get(g);
        if (margins && margins.length) {
          const m = mean(margins);
          let bg, color;
          if (m <= -10) { bg = 'rgba(107,203,119,.55)'; color = '#081519'; }
          else if (m < -AT_BENCHMARK_TOLERANCE) { bg = 'rgba(107,203,119,.28)'; color = 'var(--dash-green)'; }
          else if (m <= AT_BENCHMARK_TOLERANCE) { bg = 'rgba(185,140,214,.28)'; color = 'var(--dash-purple)'; }
          else if (m < 10) { bg = 'rgba(255,107,138,.28)'; color = 'var(--dash-pink)'; }
          else { bg = 'rgba(255,107,138,.55)'; color = '#081519'; }
          cellMap[g][bk] = { mean: m, n: margins.length, bg, color };
        }
      });
    });

    return { bucketKeys, bucketDates, bucketFmt, cellMap, ent };
  }, [filteredData, heatmapAgg, gradeOptions, entLabel]);

  const cellWidth = heatmapAgg === 'day' ? 28 : heatmapAgg === 'week' ? 38 : 56;

  /* ── Grade deep-dive ── */
  const deepDive = useMemo(() => {
    if (!gradeFocus || !filteredData.length) return null;
    const rows = filteredData.filter(r => r.count === gradeFocus).sort((a, b) => a.t - b.t);
    if (!rows.length) return null;

    const silMargins = rows.map(silMargin);
    const skmMargins = rows.map(skmMargin);
    const silAvg = mean(silMargins);
    const skmAvg = mean(skmMargins);
    const avgBm = mean(rows.map(r => r.aqua));
    const firstDate = rows[0].date;
    const lastDate = rows[rows.length - 1].date;
    const monthsSpan = Math.round((lastDate - firstDate) / (1000 * 60 * 60 * 24 * 30)) + 1;
    const contextNote = `${rows.length} lots across ${new Set(rows.map(r => r.t)).size} days · ~${monthsSpan} months`;

    const insights = [
      { tag: 'Market average', color: 'var(--dash-purple)', head: fmtPrice(avgBm), body: `Aqua Brahma market rate for Grade ${gradeFocus} over this window.` },
      { tag: 'Lots seen', color: 'var(--dash-purple)', head: fmtInt(rows.length), body: 'Bid rows for this grade in the current data.' },
      { tag: 'SIL vs market', color: silAvg < -AT_BENCHMARK_TOLERANCE ? 'var(--dash-green)' : silAvg > AT_BENCHMARK_TOLERANCE ? 'var(--dash-pink)' : 'var(--dash-blue)', head: fmtDelta(silAvg), body: silAvg < -AT_BENCHMARK_TOLERANCE ? 'SIL buys below market on this grade.' : silAvg > AT_BENCHMARK_TOLERANCE ? 'SIL paying above market — review.' : 'Tracking market closely.' },
      { tag: 'SKM vs market', color: skmAvg < -AT_BENCHMARK_TOLERANCE ? 'var(--dash-green)' : skmAvg > AT_BENCHMARK_TOLERANCE ? 'var(--dash-pink)' : 'var(--dash-coral)', head: fmtDelta(skmAvg), body: skmAvg < -AT_BENCHMARK_TOLERANCE ? 'SKM buys below market on this grade.' : skmAvg > AT_BENCHMARK_TOLERANCE ? 'SKM paying above market — review.' : 'Tracking market closely.' },
    ];

    const priceHistoryData = rows.map(r => ({ t: r.t, dateLabel: fmtDateLong(r.date), aqua: r.aqua, sil: r.sil, skm: r.skm }));

    const ent = entLabel;
    const byDay = groupBy(rows, r => r.t);
    const dayKeys = [...byDay.keys()].sort((a, b) => a - b);
    const dailyMovementData = dayKeys.map((k) => {
      const rs = byDay.get(k);
      return { t: k, dateLabel: fmtDateLong(new Date(k)), y: mean(rs.map(r => entityMargin(r, ent))), lots: rs.length };
    });
    const values = dailyMovementData.map(p => p.y);
    const avgVal = mean(values);
    const minVal = values.length ? Math.min(...values) : 0;
    const maxVal = values.length ? Math.max(...values) : 0;
    const belowDays = values.filter(v => v < -0.5).length;
    const aboveDays = values.filter(v => v > 0.5).length;

    return { insights, contextNote, priceHistoryData, dailyMovementData, ent, dailyStats: { avgVal, minVal, maxVal, belowDays, aboveDays, n: dailyMovementData.length } };
  }, [gradeFocus, filteredData, entLabel]);

  const entColorVar = deepDive?.ent === 'SIL' ? 'var(--dash-blue)' : 'var(--dash-coral)';

  /* ── Overpay log ── */
  const overpayAll = useMemo(() => {
    const ent = entLabel;
    const otherEnt = ent === 'SIL' ? 'SKM' : 'SIL';
    return filteredData.map(r => ({
      r,
      overpay: entityMargin(r, ent),
      otherMargin: entityMargin(r, otherEnt),
      otherPrice: entityPrice(r, otherEnt),
      paid: entityPrice(r, ent),
      pct: r.aqua ? (entityMargin(r, ent) / r.aqua * 100) : 0,
      otherEnt,
    })).filter(x => x.overpay > 0);
  }, [filteredData, entLabel]);

  const overpayRows = useMemo(() => {
    let list = overpayAll;
    const q = overpaySearch.trim().toLowerCase();
    if (q) {
      list = list.filter(x => fmtDateLong(x.r.date).toLowerCase().includes(q) || String(x.r.count).toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      let av, bv;
      switch (overpaySortKey) {
        case 'date': av = a.r.t; bv = b.r.t; break;
        case 'count': av = a.r.countSort; bv = b.r.countSort; break;
        case 'paid': av = a.paid; bv = b.paid; break;
        case 'aqua': av = a.r.aqua; bv = b.r.aqua; break;
        case 'pct': av = a.pct; bv = b.pct; break;
        case 'other': av = a.otherMargin; bv = b.otherMargin; break;
        default: av = a.overpay; bv = b.overpay;
      }
      return overpaySortAsc ? av - bv : bv - av;
    });
  }, [overpayAll, overpaySearch, overpaySortKey, overpaySortAsc]);

  const overpayShown = overpayRows.slice(0, ROW_CAP);

  const toggleSort = (key) => {
    if (overpaySortKey === key) setOverpaySortAsc(a => !a);
    else { setOverpaySortKey(key); setOverpaySortAsc(false); }
  };

  const exportOverpayCSV = () => {
    if (!overpayAll.length) return;
    const ent = entLabel;
    const otherEnt = ent === 'SIL' ? 'SKM' : 'SIL';
    const rows = [['Date', 'Grade', `${ent} paid`, 'Benchmark', 'Overpay', 'Overpay %', `${otherEnt} paid`, `${otherEnt} margin`]];
    [...overpayAll].sort((a, b) => b.overpay - a.overpay).forEach((x) => {
      rows.push([
        fmtDateInput(x.r.date), x.r.count,
        x.paid.toFixed(2), x.r.aqua.toFixed(2),
        x.overpay.toFixed(2), x.pct.toFixed(2),
        x.otherPrice.toFixed(2), x.otherMargin.toFixed(2),
      ]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'overpay-log.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <GradeStrategySkeleton />;

  return (
    <div className="min-h-full bg-dash-bg text-dash-ink font-grotesk antialiased">
      <div className="max-w-[1400px] mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-dash-panel p-6 rounded-xl border border-dash-line shadow-sm">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center font-grotesk font-extrabold text-sm shrink-0"
              style={{ background: 'linear-gradient(135deg, var(--dash-green) 0%, var(--dash-pink) 100%)', color: '#081519' }}
            >
              G
            </div>
            <div>
              <h1 className="text-lg font-bold text-dash-ink">Grade Strategy</h1>
              <p className="text-[10px] text-dash-mut uppercase tracking-wider">Strengths · blind spots · drill-down vs Aqua Brahma</p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-dash-panel2 border border-dash-line rounded-lg p-1" data-testid="entity-toggle">
            {ENTITY_TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setEntity(t.id)}
                className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition inline-flex items-center gap-1.5 ${entity === t.id ? 'bg-dash-panel text-dash-ink font-semibold shadow-sm' : 'text-dash-mut hover:text-dash-ink'}`}
                data-testid={`entity-tab-${t.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.dot }} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-dash-panel border border-dash-line rounded-xl p-10 text-center text-dash-coral text-sm">Error: {error}</div>
        )}

        {/* Period Filter */}
        {!error && months.length > 0 && (
          <div className="bg-dash-panel p-4 rounded-xl border border-dash-line shadow-sm">
            <PeriodFilter
              months={months.map(m => m.label)}
              fromIdx={fromIdx}
              toIdx={toIdx}
              onFromChange={setFromIdx}
              onToChange={setToIdx}
              onApply={applyPeriod}
              onReset={resetPeriod}
              periodLabel={periodLabel}
              isFiltered={isFiltered}
            />
          </div>
        )}

        {!error && (
          <>
            <div className="bg-dash-panel border border-dash-line border-dashed rounded-xl p-4 text-xs text-dash-mut leading-relaxed">
              <b className="text-dash-ink">Drill down →</b> open any panel below to investigate <b className="text-dash-ink">when</b> a grade misbehaves (heatmap), <b className="text-dash-ink">how</b> its prices move vs the market (deep-dive), or <b className="text-dash-ink">every lot</b> that paid above the market (overpay log).
            </div>

            {/* Margin heatmap */}
            <Disclosure title="Margin heatmap" tag="01" hint="Grade × time · scan for patterns" open={openHeatmap} onToggle={() => setOpenHeatmap(o => !o)}>
              {heatmap && (
                <>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold">Aggregate by</label>
                    <div className="flex gap-1">
                      {['day', 'week', 'month'].map(a => (
                        <button
                          key={a}
                          onClick={() => setHeatmapAgg(a)}
                          className={`px-3 py-1 rounded-full text-[11px] border transition ${heatmapAgg === a ? 'bg-dash-ice text-white border-dash-ice font-semibold' : 'bg-dash-panel2 border-dash-line text-dash-mut hover:text-dash-ink'}`}
                        >
                          {a[0].toUpperCase() + a.slice(1)}
                        </button>
                      ))}
                    </div>
                    <span className="ml-auto text-[11px] text-dash-mut">
                      {heatmap.bucketKeys.length} {heatmapAgg}{heatmap.bucketKeys.length === 1 ? '' : 's'} · {gradeOptions.length} grades · entity: {heatmap.ent}
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <div
                      className="grid gap-[2px] text-[10px]"
                      style={{ gridTemplateColumns: `110px repeat(${heatmap.bucketKeys.length}, minmax(${cellWidth}px, 1fr))`, minWidth: 110 + heatmap.bucketKeys.length * cellWidth }}
                    >
                      <div className="text-dash-mut uppercase text-[9px] tracking-wider font-semibold py-1">{heatmap.ent} margin</div>
                      {heatmap.bucketKeys.map(bk => (
                        <div key={bk} className="text-dash-mut uppercase text-[9px] tracking-wider font-semibold text-center py-1 whitespace-nowrap">
                          {heatmap.bucketFmt(heatmap.bucketDates.get(bk))}
                        </div>
                      ))}
                      {gradeOptions.map(g => (
                        <React.Fragment key={g}>
                          <div className="text-dash-mut text-[9px] font-semibold pl-1.5 flex items-center whitespace-nowrap">Grade {g}</div>
                          {heatmap.bucketKeys.map((bk) => {
                            const cell = heatmap.cellMap[g]?.[bk];
                            return (
                              <div
                                key={bk}
                                title={cell ? `${heatmap.bucketFmt(heatmap.bucketDates.get(bk))} · Grade ${g}\n${cell.n} lot${cell.n === 1 ? '' : 's'}\nAvg vs market: ${fmtDelta(cell.mean)}` : undefined}
                                className="rounded-sm flex items-center justify-center font-mono"
                                style={{ minHeight: 24, background: cell ? cell.bg : 'var(--dash-panel2)', color: cell ? cell.color : 'var(--dash-mut)' }}
                              >
                                {cell ? (cell.mean >= 0 ? '+' : '') + Math.round(cell.mean) : '·'}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-dash-mut">
                    <span className="inline-flex items-center gap-1.5"><span className="w-4 h-2.5 rounded-sm inline-block" style={{ background: 'rgba(107,203,119,.6)' }} /> ≤ −₹10 (strong savings)</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-4 h-2.5 rounded-sm inline-block" style={{ background: 'rgba(107,203,119,.3)' }} /> −₹10 to 0</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-4 h-2.5 rounded-sm inline-block" style={{ background: 'rgba(185,140,214,.3)' }} /> at market rate</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-4 h-2.5 rounded-sm inline-block" style={{ background: 'rgba(255,107,138,.3)' }} /> 0 to +₹10</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-4 h-2.5 rounded-sm inline-block" style={{ background: 'rgba(255,107,138,.6)' }} /> ≥ +₹10 (heavy overpay)</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-4 h-2.5 rounded-sm inline-block bg-dash-panel2 border border-dash-line" /> no data</span>
                  </div>
                </>
              )}
            </Disclosure>

            {/* Grade deep-dive */}
            <Disclosure title="Grade deep-dive" tag="02" hint="Price history & daily movement for one grade" open={openDeepDive} onToggle={() => setOpenDeepDive(o => !o)}>
              {deepDive && (
                <>
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold">Focus grade</label>
                    <select
                      value={gradeFocus}
                      onChange={(e) => setGradeFocus(e.target.value)}
                      className="bg-dash-panel2 border border-dash-line rounded-lg px-3 py-1.5 text-xs text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                    >
                      {gradeOptions.map(g => <option key={g} value={g}>Grade {g}</option>)}
                    </select>
                    <span className="text-xs text-dash-mut">{deepDive.contextNote}</span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    {deepDive.insights.map((it, i) => (
                      <div key={i} className="bg-dash-panel2 border border-dash-line rounded-lg p-3.5" style={{ borderLeft: `3px solid ${it.color}` }}>
                        <div className="text-[9px] uppercase tracking-wider text-dash-mut font-semibold mb-1.5">{it.tag}</div>
                        <div className="text-lg font-bold text-dash-ink mb-1">{it.head}</div>
                        <div className="text-[11px] text-dash-mut leading-snug">{it.body}</div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-dash-panel2 border border-dash-line rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-dash-ink mb-0.5">Price history for Grade {gradeFocus}</h4>
                    <p className="text-xs text-dash-mut mb-3">SIL and SKM plotted against the Aqua Brahma market rate over time. Lines close together = everyone is tracking the market.</p>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={deepDive.priceHistoryData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                          <CartesianGrid stroke="var(--dash-line)" strokeDasharray="3 3" />
                          <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={fmtAxisDate} tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" />
                          <YAxis tickFormatter={v => '₹' + v} tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" />
                          <Tooltip content={<PriceHistoryTooltip />} />
                          <Legend wrapperStyle={{ fontSize: 11, color: 'var(--dash-mut)' }} />
                          <Line type="monotone" dataKey="aqua" name="Benchmark" stroke="var(--dash-purple)" dot={false} strokeWidth={2} />
                          <Line type="monotone" dataKey="sil" name="SIL" stroke="var(--dash-blue)" dot={false} strokeWidth={1.5} />
                          <Line type="monotone" dataKey="skm" name="SKM" stroke="var(--dash-coral)" dot={false} strokeWidth={1.5} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bg-dash-panel2 border border-dash-line rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-dash-ink mb-0.5">Daily movement vs market</h4>
                    <p className="text-xs text-dash-mut mb-2">
                      Showing <b className="text-dash-ink">Grade {gradeFocus}</b> only — each point is the exact ₹/kg difference between {deepDive.ent}'s price and the Aqua Brahma market rate on that day. Purple dashed line = market rate.
                    </p>
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <span className="font-mono text-[11px] text-dash-mut">Showing {deepDive.ent}</span>
                      <div className="text-[11px] text-dash-mut ml-auto">
                        {deepDive.dailyStats.n} days · avg{' '}
                        <b style={{ color: deepDive.dailyStats.avgVal < -0.5 ? 'var(--dash-green)' : deepDive.dailyStats.avgVal > 0.5 ? 'var(--dash-pink)' : 'var(--dash-ink)' }}>
                          {fmtDelta(deepDive.dailyStats.avgVal)}
                        </b>{' '}
                        · {deepDive.dailyStats.belowDays} below · {deepDive.dailyStats.aboveDays} above
                        {' '}· worst <b className="text-dash-pink">+₹{deepDive.dailyStats.maxVal.toFixed(1)}</b>
                        {' '}· best <b className="text-dash-green">−₹{Math.abs(deepDive.dailyStats.minVal).toFixed(1)}</b>
                      </div>
                    </div>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={deepDive.dailyMovementData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                          <CartesianGrid stroke="var(--dash-line)" strokeDasharray="3 3" />
                          <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={fmtAxisDate} tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" />
                          <YAxis tickFormatter={v => (v > 0 ? '+' : '') + '₹' + v} tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" label={{ value: '₹/kg above or below market', angle: -90, position: 'insideLeft', fill: 'var(--dash-mut)', fontSize: 11 }} />
                          <Tooltip content={<DailyTooltip ent={deepDive.ent} />} />
                          <ReferenceLine y={0} stroke="var(--dash-purple)" strokeDasharray="5 5" label={{ value: 'Market rate', position: 'insideTopRight', fill: 'var(--dash-purple)', fontSize: 10 }} />
                          <Line
                            type="monotone" dataKey="y" stroke={entColorVar} strokeWidth={1.8}
                            dot={(props) => {
                              const { cx, cy, payload, index } = props;
                              const color = payload.y < -0.5 ? 'var(--dash-green)' : payload.y > 0.5 ? 'var(--dash-pink)' : entColorVar;
                              return <circle key={index} cx={cx} cy={cy} r={3} fill={color} stroke="none" />;
                            }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}
              {!deepDive && <div className="text-sm text-dash-mut italic py-4 text-center">No grades available.</div>}
            </Disclosure>

            {/* Overpay log */}
            <Disclosure title="Overpay log" tag="03" hint="Every lot paid above market · sortable, searchable, exportable" open={openOverpay} onToggle={() => setOpenOverpay(o => !o)}>
              <div className="flex justify-between items-center gap-3 mb-3 flex-wrap bg-dash-panel2 border border-dash-line rounded-lg px-4 py-3 text-xs text-dash-mut">
                <div>Every lot where <b className="text-dash-ink">{entLabel}</b> paid <b className="text-dash-pink">above</b> the Aqua Brahma market rate — sorted worst first. Other entity's price vs market shown for context.</div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-dash-mut" />
                    <input
                      type="text"
                      value={overpaySearch}
                      onChange={(e) => setOverpaySearch(e.target.value)}
                      placeholder="Search date or grade..."
                      className="bg-dash-panel border border-dash-line rounded pl-7 pr-2.5 py-1.5 text-xs text-dash-ink min-w-[170px] focus:outline-none focus:ring-2 focus:ring-dash-ice"
                    />
                  </div>
                  <button
                    onClick={exportOverpayCSV}
                    className="bg-dash-panel2 hover:bg-dash-line border border-dash-line text-dash-ink px-3 py-1.5 rounded text-xs inline-flex items-center gap-1.5"
                    data-testid="btn-export-overpay-csv"
                  >
                    <Download className="w-3.5 h-3.5" />
                    CSV
                  </button>
                </div>
              </div>

              <div className="border border-dash-line rounded-lg overflow-auto max-h-[640px]">
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 bg-dash-panel2 z-10">
                    <tr>
                      {OVERPAY_COLUMNS.map(c => (
                        <th
                          key={c.key}
                          onClick={() => toggleSort(c.key)}
                          className="p-2.5 text-left uppercase text-[10px] tracking-wider text-dash-mut font-semibold cursor-pointer hover:text-dash-ink whitespace-nowrap select-none"
                        >
                          {c.label}{overpaySortKey === c.key ? (overpaySortAsc ? ' ▲' : ' ▼') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dash-line text-dash-ink">
                    {overpayShown.length === 0 && (
                      <tr><td colSpan={OVERPAY_COLUMNS.length} className="p-8 text-center text-dash-green italic">🎯 No overpay lots found {overpaySearch ? 'matching search' : 'in this window'}.</td></tr>
                    )}
                    {overpayShown.map((x, i) => {
                      const otherCls = x.otherMargin < -AT_BENCHMARK_TOLERANCE ? 'text-dash-green' : x.otherMargin > AT_BENCHMARK_TOLERANCE ? 'text-dash-pink' : 'text-dash-purple';
                      return (
                        <tr key={i} className="hover:bg-dash-panel2 transition-colors">
                          <td className="p-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[rgba(255,107,138,.15)] text-dash-pink">+₹{x.overpay.toFixed(2)}</span></td>
                          <td className="p-2.5 font-mono whitespace-nowrap">{fmtDateLong(x.r.date)}</td>
                          <td className="p-2.5 font-mono">{x.r.count}</td>
                          <td className="p-2.5 text-right font-mono">{fmtPricePrecise(x.paid)}</td>
                          <td className="p-2.5 text-right font-mono text-dash-purple">{fmtPricePrecise(x.r.aqua)}</td>
                          <td className="p-2.5 text-right font-mono text-dash-pink">+{x.pct.toFixed(2)}%</td>
                          <td className="p-2.5 font-mono whitespace-nowrap">
                            {x.otherEnt}: {fmtPricePrecise(x.otherPrice)}{' '}
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-dash-panel2 ${otherCls}`}>{fmtDelta(x.otherMargin)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-center text-[11px] text-dash-mut italic py-3">
                {overpaySearch
                  ? `Showing ${overpayRows.length} match${overpayRows.length === 1 ? '' : 'es'} of ${overpayAll.length} total flagged lots`
                  : overpayAll.length > ROW_CAP
                    ? `Showing first ${ROW_CAP} of ${fmtInt(overpayAll.length)} flagged lots · export CSV for full list`
                    : overpayAll.length > 0 ? `${fmtInt(overpayAll.length)} flagged lot${overpayAll.length === 1 ? '' : 's'} total` : ''}
              </div>
            </Disclosure>
          </>
        )}
      </div>

      <FloatingFAB guideContent={GUIDE} />
    </div>
  );
};

export default GradeStrategyReact;
